import { fabric } from 'fabric';
import { v4 as uuidv4 } from 'uuid';
import DrawService from './DrawService';

export const DRAW_OVERLAY_DEFAULT_WIDTH_PX = 1000;
const DRAW_OVERLAY_MIN_HEIGHT_PX = 400;
const DRAW_OVERLAY_OBJECT_START_TOP_PX = 100;

/** Uses model.questionWidth when set to a positive number; otherwise {@link DRAW_OVERLAY_DEFAULT_WIDTH_PX}. */
export function getDrawOverlayWidthPx(model) {
  const w = Number(model?.questionWidth);
  return Number.isFinite(w) && w > 0 ? Math.round(w) : DRAW_OVERLAY_DEFAULT_WIDTH_PX;
}

/**
 * Same response shape as Draw (drawing, elements, text). Width from model.questionWidth or 1000px;
 * height comes from rendered question text (set on model.graphHeight before init / via resizeToHeight).
 */
export default class DrawOverlayService extends DrawService {
  type = 'DrawOverlay';

  getLabel = () => {
    return 'drawOverlay';
  }

  initialize() {
    this.model.graphWidth = getDrawOverlayWidthPx(this.model);
    if (!this.model.graphHeight || this.model.graphHeight < DRAW_OVERLAY_MIN_HEIGHT_PX) {
      this.model.graphHeight = DRAW_OVERLAY_MIN_HEIGHT_PX;
    }
    this.model.hasGraph = false;
    this.model.imageSource = this.model.imageSource || null;
    super.initialize();
  }

  runtimeObjectReady() {
    this.model.hasGraph = false;
    this.model.graphWidth = getDrawOverlayWidthPx(this.model);
    super.runtimeObjectReady();
    if (this.canvas) {
      this.canvas.setBackgroundColor('transparent', () => this.canvas.renderAll());
    }
    if (this.drawCanvas) {
      this.drawCanvas.setBackgroundColor('transparent', () => this.drawCanvas.renderAll());
    }
  }

  resizeToHeight = (height) => {
    const h = Math.max(Math.ceil(height), DRAW_OVERLAY_MIN_HEIGHT_PX);
    this.model.graphHeight = h;
    if (!this.canvas || !this.drawCanvas) {
      return;
    }
    const w = this.getWidth();
    this.canvas.setWidth(w);
    this.canvas.setHeight(h);
    this.canvas.calcOffset();
    this.drawCanvas.setWidth(w);
    this.drawCanvas.setHeight(h);
    this.drawCanvas.calcOffset();
    this.drawGraph();
    this.showResponse();
  }

  getOverlayStartTop = () => {
    const topPadding = 120;
    const maxTop = Math.max(DRAW_OVERLAY_OBJECT_START_TOP_PX, this.getHeight() - topPadding);
    return Math.min(DRAW_OVERLAY_OBJECT_START_TOP_PX + 30 * Math.random(), maxTop);
  }

  addShape = (type) => {
    let shape = null;
    const top = this.getOverlayStartTop();
    const left = Math.max(40, parseInt(this.getWidth() / 2 - 60, 10));
    const randomX = 40 * Math.random();

    if (type === this.TRIANGLE) {
      shape = new fabric.Triangle({
        width: 80,
        height: 80,
      });
    } else if (type === this.SQUARE) {
      shape = new fabric.Rect({
        width: 80,
        height: 80,
      });
    } else if (type === this.CIRCLE) {
      shape = new fabric.Circle({
        radius: 40,
        originX: 'center',
        originY: 'center',
      });
    } else if (type === this.STAR) {
      const starPoints = this.getStarPoints(5, 45, 20);
      shape = new fabric.Polygon(starPoints, {
      }, false);
    }

    if (shape) {
      shape.top = top;
      shape.left = left + randomX;
      shape.stroke = this.currentStrokeColor;
      shape.fill = this.currentFillColor;
      shape.strokeWidth = this.currentStrokeWidth;
      shape.strokeUniform = true;
      this.drawCanvas.add(shape);
      this.drawCanvas.renderAll();

      this.setElementEditMode(this.DRAW);
      this.setDrawingMode(false);
      this.selectToolbarButton('shapeToolButton');

      this.drawCanvas.setActiveObject(shape);
      this.drawCanvas.renderAll();

      this.updateState();
      this.updateResponseModel();
    }
  }

  onClickTextTool = () => {
    const textId = uuidv4();
    const top = parseInt(this.getOverlayStartTop(), 10);
    const left = parseInt(Math.max(40, this.getWidth() / 2 - 40) + 40 * Math.random(), 10);

    const textContainer = {
      textId,
      top,
      left,
      text: '',
      lessonElementId: this.model.lessonElementId,
      opacity: 1,
    };

    this.textManager.addTextContainer(textContainer, this.model.lessonElementId);
    this.updateState();
    this.updateResponseModel();
  };
}
